import { styled } from '@/styles/stitches.config';
import { useDropdownMenu } from '@/utils/use-dropdown-menu';
import { Menu, MenuItem } from './menu';
import { LinkTo } from '@/components/link-to';
import { IconDownload } from '@/assets/svg/icon-download';
import { Button } from '../elements/button';

interface SidebarLink {
  href?: string;
  children: React.ReactNode;
  download: string;
  options?: { href: string; label: string }[];
}

interface SidebarLinksProps {
  links: SidebarLink[];
}

interface SidebarDropdownProps {
  direction?: 'up' | 'down';
  alignment?: 'left' | 'right';
  items: { href: string; label: string }[];
  download: string;
  children: React.ReactNode;
}

const List = styled('ul', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'center',
  listStyleType: 'none',
  columnGap: '10px',
  padding: 0,
  margin: 0,

  '@bp2': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
});

const ListItem = styled('li', {
  paddingBlock: '$3',
});

const ListItemText = styled('span', {
  lineHeight: 1.4,
});

const SidebarDropdownRoot = styled('div', {
  position: 'relative',
});

const ButtonWithIcon = styled(Button, {
  display: 'flex',
  alignItems: 'center',
  justifySelf: 'flex-start',
  gap: '9px',

  '&:hover': {
    textDecoration: 'underline',
  },
  '&:focus-visible, &.focus-visible': {
    outlineColor: 'currentColor',
    outlineStyle: 'dotted',
    outlineWidth: 3,
  },
});

function SidebarDropdown(props: SidebarDropdownProps) {
  const { alignment = 'left', direction = 'down', items, download, children } = props;
  const { getButtonProps, getMenuProps, getRootProps } = useDropdownMenu<HTMLDivElement>();
  return (
    <SidebarDropdownRoot {...getRootProps()}>
      <ButtonWithIcon
        {...getButtonProps()}
        css={{
          border: 'none',
          paddingInline: '4px',
          fontSize: '16px',
          lineHeight: '45px',
        }}
      >
        <IconDownload />
        <ListItemText>{children}</ListItemText>
      </ButtonWithIcon>

      <Menu alignment={alignment} direction={direction} {...getMenuProps()}>
        {items.map((item) => {
          return (
            <MenuItem key={item.href}>
              <LinkTo download={download} href={item.href}>
                {item.label}
              </LinkTo>
            </MenuItem>
          );
        })}
      </Menu>
    </SidebarDropdownRoot>
  );
}

export function SidebarLinks({ links }: SidebarLinksProps) {
  return (
    <List>
      {links.map((el, index) => {
        if (el.options) {
          return (
            <ListItem key={index}>
              <SidebarDropdown download={el.download} items={el.options}>
                {el.children}
              </SidebarDropdown>
            </ListItem>
          );
        }

        if (el.href) {
          return (
            <ListItem key={index}>
              <ButtonWithIcon
                as={LinkTo}
                {...el}
                href={el.href}
                css={{
                  paddingInline: '4px',
                  borderRadius: 0,
                  maxWidth: '17.5rem',
                  background: 'none',
                  fontSize: '16px',
                  lineHeight: '45px',
                }}
              >
                <IconDownload />
                <ListItemText>{el.children}</ListItemText>
              </ButtonWithIcon>
            </ListItem>
          );
        }
      })}
    </List>
  );
}
